// Meta (Facebook) Pixel + Hotmart high-precision tracking helper.
//
// Responsibilities:
//   1. Initialize the Meta Pixel base code (once) and fire PageView.
//   2. Capture UTM parameters + fbclid from the landing URL on first load
//      and persist them in sessionStorage so they survive the funnel flow.
//   3. Expose helpers to track standard / custom events.
//   4. Build a high-precision Hotmart checkout URL with:
//      - All UTM params forwarded individually
//      - A combined `sck` / `src` pipe-separated identifier
//      - `fbclid`, `fbp`, `fbc` for CAPI / browser-side dedupe
//   5. Be fault-tolerant — tracking errors must NEVER block redirection.

declare global {
  interface Window {
    fbq?: (...args: any[]) => void
    _fbq?: any
  }
}

const PIXEL_ID = '788851460829819'
const STORAGE_KEY = 'tphlanding_utms_v1'
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

type UtmKey = (typeof UTM_KEYS)[number]
type TrackingData = Partial<Record<UtmKey, string>> & {
  fbclid?: string
  landingUrl?: string
  landedAt?: string
}

let initialized = false

// ---------------------------------------------------------------------------
// Pixel initialization
// ---------------------------------------------------------------------------
export function initPixel() {
  if (typeof window === 'undefined' || initialized) return
  initialized = true

  try {
    // Capture UTMs and fbclid from the landing URL ONCE. After this the URL
    // may not contain them (internal SPA navigation), so we persist them in
    // sessionStorage for the rest of the funnel.
    captureLandingParams()
  } catch (err) {
    // Tracking must never break the app — swallow errors silently.
    console.warn('[pixel] captureLandingParams failed', err)
  }

  try {
    // Inject the Meta Pixel base code
    ;(function (f: any, b: Document, e: string, v: string) {
      if (f.fbq) return
      const n: any = (f.fbq = function () {
        // eslint-disable-next-line prefer-rest-params
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments)
      })
      if (!f._fbq) f._fbq = n
      n.push = n
      n.loaded = true
      n.version = '2.0'
      n.queue = []
      const t = b.createElement(e) as HTMLScriptElement
      t.async = true
      t.src = v
      const s = b.getElementsByTagName(e)[0]
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s)
      } else {
        ;(b.head || b.documentElement).appendChild(t)
      }
    })(
      window,
      document,
      'script',
      'https://connect.facebook.net/en_US/fbevents.js',
    )

    if (window.fbq) {
      window.fbq('init', PIXEL_ID)
      window.fbq('track', 'PageView')
    }

    // Add the <noscript> fallback pixel image once
    if (!document.getElementById('fb-pixel-noscript')) {
      const noscript = document.createElement('noscript')
      noscript.id = 'fb-pixel-noscript'
      const img = document.createElement('img')
      img.height = 1
      img.width = 1
      img.style.display = 'none'
      img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`
      noscript.appendChild(img)
      document.body.appendChild(noscript)
    }
  } catch (err) {
    console.warn('[pixel] initPixel failed', err)
  }
}

// ---------------------------------------------------------------------------
// Event helpers
// ---------------------------------------------------------------------------
export function trackPageView() {
  try {
    if (typeof window === 'undefined' || !window.fbq) return
    window.fbq('track', 'PageView')
  } catch (err) {
    console.warn('[pixel] trackPageView failed', err)
  }
}

export function trackEvent(name: string, params?: Record<string, any>) {
  try {
    if (typeof window === 'undefined' || !window.fbq) return
    const standardEvents = new Set([
      'ViewContent',
      'Search',
      'AddToCart',
      'AddToWishlist',
      'InitiateCheckout',
      'AddPaymentInfo',
      'Purchase',
      'Lead',
      'CompleteRegistration',
      'Contact',
      'CustomizeProduct',
      'Donate',
      'FindLocation',
      'Schedule',
      'StartTrial',
      'SubmitApplication',
      'Subscribe',
    ])
    if (standardEvents.has(name)) {
      window.fbq('track', name, params)
    } else {
      window.fbq('trackCustom', name, params)
    }
  } catch (err) {
    console.warn('[pixel] trackEvent failed', err)
  }
}

// ---------------------------------------------------------------------------
// Cookie + URL helpers (CAPI support)
// ---------------------------------------------------------------------------
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  try {
    const match = document.cookie.match(
      new RegExp(
        '(?:^|;\\s*)' +
          name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') +
          '=([^;]*)',
      ),
    )
    return match ? decodeURIComponent(match[1]) : null
  } catch {
    return null
  }
}

function getUrlParam(name: string): string | null {
  if (typeof window === 'undefined') return null
  try {
    return new URLSearchParams(window.location.search).get(name)
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// UTM capture + persistence
// ---------------------------------------------------------------------------
function captureLandingParams() {
  if (typeof window === 'undefined') return
  const stored = getStoredTracking()

  // Only overwrite if there are NEW params in the current URL — preserves the
  // very first attribution (so internal navigations don't wipe it out).
  let hasNew = false
  const next: TrackingData = { ...stored }

  UTM_KEYS.forEach((key) => {
    const v = getUrlParam(key)
    if (v) {
      next[key] = v
      hasNew = true
    }
  })

  const fbclid = getUrlParam('fbclid')
  if (fbclid) {
    next.fbclid = fbclid
    hasNew = true
  }

  if (hasNew || !stored.landedAt) {
    next.landingUrl = window.location.href
    next.landedAt = new Date().toISOString()
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // sessionStorage may be unavailable (private mode); ignore
    }
  }
}

function getStoredTracking(): TrackingData {
  if (typeof window === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as TrackingData) : {}
  } catch {
    return {}
  }
}

// ---------------------------------------------------------------------------
// Hotmart checkout URL builder (HIGH-PRECISION TRACKING)
// ---------------------------------------------------------------------------
/**
 * Build an enhanced Hotmart checkout URL with full attribution tracking.
 *
 * Appends to the base Hotmart URL:
 *   - Individual UTM params (utm_source, utm_medium, utm_campaign, utm_content, utm_term)
 *   - fbclid (Facebook click ID)
 *   - sck + src (pipe-joined combined identifier — Hotmart's native source fields)
 *   - fbp (Facebook browser ID from _fbp cookie) — CAPI support
 *   - fbc (Facebook click ID from _fbc cookie) — CAPI support
 *
 * Fault-tolerant: on any error returns the original URL unchanged so the user
 * is still redirected to checkout.
 */
export function buildCheckoutUrl(baseUrl: string): string {
  try {
    const tracking = getStoredTracking()
    const url = new URL(baseUrl)

    // 1. Forward all UTM params individually
    UTM_KEYS.forEach((key) => {
      if (tracking[key]) {
        url.searchParams.set(key, tracking[key] as string)
      }
    })

    // 2. Forward fbclid
    if (tracking.fbclid) {
      url.searchParams.set('fbclid', tracking.fbclid)
    }

    // 3. Combined SCK / SRC — Hotmart's native source identifiers
    const combinedParts = UTM_KEYS.map((k) => tracking[k]).filter(
      (v): v is string => Boolean(v),
    )
    const combined =
      combinedParts.length > 0
        ? combinedParts.join('|')
        : 'organico|protocolo-7-dias'

    url.searchParams.set('sck', combined)
    url.searchParams.set('src', combined)

    // 4. CAPI cookies (_fbp / _fbc) for server-side Meta tracking dedupe
    const fbp = getCookie('_fbp')
    const fbc = getCookie('_fbc')
    if (fbp) url.searchParams.set('fbp', fbp)
    if (fbc) url.searchParams.set('fbc', fbc)

    // If we have fbclid but no _fbc cookie yet, synthesize one (Meta's format)
    // so Hotmart CAPI still receives an attribution token.
    if (!fbc && tracking.fbclid) {
      const synthFbc = `fb.1.${Date.now()}.${tracking.fbclid}`
      url.searchParams.set('fbc', synthFbc)
    }

    return url.toString()
  } catch (err) {
    console.warn('[pixel] buildCheckoutUrl failed, returning base URL', err)
    return baseUrl
  }
}
